import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

/**
 * Realtime message notifications - deferred for performance.
 * PERFORMANCE: Delays subscription setup to avoid blocking initial render.
 */
export function useRealtimeMessageNotifications() {
  const { data: user } = useCurrentUser();
  const queryClient = useQueryClient();
  const subscriptionDeferred = useRef(false);

  useEffect(() => {
    if (!user?.id) return;

    // Defer subscription to avoid blocking initial render
    // This is non-critical - notifications can wait 1 second
    const setupDelay = subscriptionDeferred.current ? 0 : 1000;
    subscriptionDeferred.current = true;

    let channel: ReturnType<typeof supabase.channel> | null = null;
    
    const setupSubscription = () => {
      // Subscribe to all new messages across all conversations where user is a participant
      channel = supabase
        .channel('new-messages-notifications')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'direct_messages',
          },
          async (payload) => {
            const newMessage = payload.new;
            
            // Only show notification if the message was sent by someone else
            if (newMessage.sender_id !== user.id) {
              // Check if user is a participant in this conversation
              const { data: isParticipant } = await supabase
                .from('conversation_participants')
                .select('id')
                .eq('conversation_id', newMessage.conversation_id)
                .eq('user_id', user.id)
                .single();

              if (isParticipant) {
                // Get sender info
                const { data: sender } = await supabase
                  .from('profiles')
                  .select('full_name, avatar_url')
                  .eq('id', newMessage.sender_id)
                  .single();

                const senderName = sender?.full_name || 'Someone';
                const messagePreview = newMessage.content.length > 50 
                  ? newMessage.content.substring(0, 50) + '...' 
                  : newMessage.content;

                // Show toast notification
                toast.success(`New message from ${senderName}`, {
                  description: messagePreview,
                  duration: 4000,
                });

                // Invalidate queries to update UI
                queryClient.invalidateQueries({ queryKey: ['messaging-data'] });
                queryClient.invalidateQueries({ queryKey: ['conversations'] });
                queryClient.invalidateQueries({ queryKey: ['messages', newMessage.conversation_id] });
              }
            }
          }
        )
        .subscribe();
    };

    const timeout = setTimeout(setupSubscription, setupDelay);

    return () => {
      clearTimeout(timeout);
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [user?.id, queryClient]);
}
